/**
 * Normalize Google Sheets formulas between locale argument separators.
 * v1: swap , and ; outside double-quoted strings only.
 */

/**
 * @param {string} formula
 * @param {'comma' | 'semicolon' | ',' | ';'} targetDelimiter
 * @returns {string}
 */
export function convertFormulaDelimiter(formula, targetDelimiter) {
  const text = String(formula ?? '');
  if (text.length === 0) return text;

  const toComma =
    targetDelimiter === 'comma' || targetDelimiter === ',';
  const fromChar = toComma ? ';' : ',';
  const toChar = toComma ? ',' : ';';

  let result = '';
  let inString = false;

  for (let i = 0; i < text.length; i += 1) {
    const ch = text[i];
    if (ch === '"') {
      if (inString && text[i + 1] === '"') {
        result += '""';
        i += 1;
        continue;
      }
      inString = !inString;
      result += ch;
      continue;
    }

    if (!inString && ch === fromChar) {
      result += toChar;
      continue;
    }

    result += ch;
  }

  return result;
}

/**
 * @param {string} formula
 * @param {string} localeCode
 * @param {Array<{ code: string, argumentSeparator: string }>} locales
 * @returns {string}
 */
export function normalizeFormulaToCanonical(formula, localeCode, locales) {
  const locale = locales.find((entry) => entry.code === localeCode) || locales.find((entry) => entry.code === 'en_US');
  const separator = locale?.argumentSeparator || ',';
  if (separator === ',') {
    return normalizeFormulaWhitespace(formula);
  }
  return normalizeFormulaWhitespace(convertFormulaDelimiter(formula, 'comma'));
}

/**
 * @param {string} formula
 * @param {string} localeCode
 * @param {Array<{ code: string, argumentSeparator: string }>} locales
 * @returns {string}
 */
export function formatFormulaForLocale(formula, localeCode, locales) {
  const canonical = normalizeFormulaToCanonical(formula, 'en_US', locales);
  const locale = locales.find((entry) => entry.code === localeCode) || locales.find((entry) => entry.code === 'en_US');
  const separator = locale?.argumentSeparator || ',';
  if (separator === ',') {
    return canonical;
  }
  return convertFormulaDelimiter(canonical, 'semicolon');
}

/**
 * @param {string} formula
 * @returns {string}
 */
export function normalizeFormulaWhitespace(formula) {
  return String(formula ?? '')
    .trim()
    .replace(/\s+/g, ' ')
    .replace(/\s*,\s*/g, ',')
    .replace(/\s*;\s*/g, ';');
}
